import { useEffect, useState, useCallback } from 'react';
import { Button, Card, Form, Input, Modal, Space, Table, message, Popconfirm, Switch, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from './api';
import type { InsuranceType } from './types';

export default function InsuranceTypesPage() {
  const [items, setItems] = useState<InsuranceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<InsuranceType | null>(null);
  const [form] = Form.useForm<{ title: string; category: string; mandatory: boolean; description?: string }>();

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/insurance-types');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente forsikringstyper');
      }
      const raw = (await res.json()) as any[];
      const normalized: InsuranceType[] = raw.map((item) => ({
        id: String(item.id ?? ''),
        title: item.title ?? item.navn ?? '',
        category: item.category ?? item.kategori ?? '',
        mandatory: Boolean(item.mandatory ?? item.lovpligtig ?? false),
        description: item.description ?? item.beskrivelse ?? null
      }));
      setItems(normalized);
    } catch (error: any) {
      console.error('Failed to load insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback(
    (record: InsuranceType) => {
      setEditing(record);
      form.setFieldsValue({
        title: record.title,
        category: record.category,
        mandatory: record.mandatory,
        description: record.description || ''
      });
      setModalOpen(true);
    },
    [form]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        title: values.title,
        category: values.category,
        mandatory: values.mandatory,
        description: values.description || null
      };

      if (editing) {
        const res = await api(`/api/insurance-types/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error((data as any)?.error || 'Kunne ikke opdatere forsikringstype');
        }
        message.success('Forsikringstype opdateret');
      } else {
        const res = await api('/api/insurance-types', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error((data as any)?.error || 'Kunne ikke oprette forsikringstype');
        }
        message.success('Forsikringstype oprettet');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await loadItems();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke gemme forsikringstype');
    } finally {
      setSaving(false);
    }
  }, [editing, form, loadItems]);

  const handleDelete = useCallback(
    async (record: InsuranceType) => {
      try {
        const res = await api(`/api/insurance-types/${record.id}`, { method: 'DELETE' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as any)?.error || 'Kunne ikke slette forsikringstype');
        }
        message.success('Forsikringstype slettet');
        await loadItems();
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke slette forsikringstype');
      }
    },
    [loadItems]
  );

  const columns: ColumnsType<InsuranceType> = [
    {
      title: 'Titel',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' })
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => a.category.localeCompare(b.category, 'da', { sensitivity: 'base' })
    },
    {
      title: 'Lovpligtig',
      dataIndex: 'mandatory',
      key: 'mandatory',
      render: (value: boolean) => (value ? 'Ja' : 'Nej'),
      filters: [
        { text: 'Ja', value: true },
        { text: 'Nej', value: false }
      ],
      onFilter: (value, record) => record.mandatory === value
    },
    {
      title: 'Beskrivelse',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | null) => value || '—'
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 200,
      render: (_text, record) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(record)}>
            Rediger
          </Button>
          <Popconfirm
            title="Slet forsikringstype"
            description={`Er du sikker på at du vil slette \"${record.title}\"?`}
            okText="Ja, slet"
            cancelText="Annuller"
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger>
              Slet
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="Forsikringstyper"
      extra={
        <Button type="primary" onClick={openCreate}>
          Ny forsikringstype
        </Button>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />

      <Modal
        open={modalOpen}
        title={editing ? 'Rediger forsikringstype' : 'Ny forsikringstype'}
        okText="Gem"
        cancelText="Annuller"
        onOk={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        confirmLoading={saving}
        destroyOnClose
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ title: '', category: '', mandatory: false, description: '' }}
        >
          <Form.Item
            label="Titel"
            name="title"
            rules={[{ required: true, message: 'Angiv titel' }]}
          >
            <Input autoFocus maxLength={200} />
          </Form.Item>
          <Form.Item
            label="Kategori"
            name="category"
            rules={[{ required: true, message: 'Angiv kategori' }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item label="Lovpligtig" name="mandatory" valuePropName="checked">
            <Switch checkedChildren="Ja" unCheckedChildren="Nej" />
          </Form.Item>
          <Form.Item label="Beskrivelse" name="description">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
